/// Renders waypoints (NAVAIDs and fixes) as small radar markers with
/// callsign labels. NAVAIDs use a hexagon glyph, fixes a triangle —
/// matching the visual convention iOS established in GameScene.swift.

import { Container, Graphics, Text } from 'pixi.js'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type { Waypoint } from '../types/LevelData'
import { radarTheme } from './theme'

export class WaypointsLayer extends Container {
  constructor(waypoints: Waypoint[], projector: CoordinateProjector) {
    super()
    for (const wp of waypoints) {
      this.addChild(buildWaypoint(wp, projector))
    }
  }
}

function buildWaypoint(wp: Waypoint, projector: CoordinateProjector): Container {
  const pos = projector.project(wp.coordinate, true)
  const node = new Container()
  node.position.set(pos.x, pos.y)

  const glyph = new Graphics()
  if (wp.type === 'navaid') {
    drawHexagon(glyph, 5)
    glyph.stroke({ width: 1.2, color: radarTheme.vor, alpha: 0.85 })
  } else {
    drawTriangle(glyph, 5)
    glyph.stroke({ width: 1.2, color: radarTheme.fix, alpha: 0.85 })
  }
  node.addChild(glyph)

  const label = new Text({
    text: wp.name,
    style: {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 10,
      fill: wp.type === 'navaid' ? radarTheme.vor : radarTheme.fix,
    },
  })
  label.alpha = 0.85
  label.position.set(7, -6)
  node.addChild(label)

  return node
}

function drawHexagon(g: Graphics, r: number): void {
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 6
    const x = Math.cos(a) * r
    const y = Math.sin(a) * r
    if (i === 0) g.moveTo(x, y)
    else g.lineTo(x, y)
  }
  g.closePath()
}

function drawTriangle(g: Graphics, r: number): void {
  for (let i = 0; i < 3; i++) {
    const a = (2 * Math.PI / 3) * i - Math.PI / 2
    const x = Math.cos(a) * r
    const y = Math.sin(a) * r
    if (i === 0) g.moveTo(x, y)
    else g.lineTo(x, y)
  }
  g.closePath()
}
